import { useCallback, useEffect, useState } from "react";
import SparketModal from "components/common/modal/modal.tsx";
import { sparketLightGray, EDIT_PROFILE_PATH } from "utils/constants.ts";
import AmountSelector from "components/commerce/amount-selector.tsx";
import { toast } from "react-toastify";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { LongButton } from "components/contests/contest-display-items/shared/buttons";
import BlockyLightText from "components/custom-texts/blocky-light-text.tsx";
import GreyModalSection from "components/commerce/grey-modal-section.tsx";
import { fetchRealWallet, getRealWalletState } from "state/slices/user-real-wallet-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import BalanceDisplay from "components/common/real-money-wallet/balance-display.tsx";
import { getWithdrawFundsModalState, hideWithdrawFundsModal } from "state/slices/withdraw-funds-modal-slice";
import { ErrorResponseBody } from "../../../interfaces/error-response-body";
import { ErrorCodes } from "../../../interfaces/error-codes";
import PaynoteFundingSourceSelector from "components/commerce/v2/paynote-funding-source-selector.tsx";
import { showGlobalLoadingOverlay, hideGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice";
import { PaynoteFundingSource } from "interfaces/commerce/paynote/paynote-funding-source.ts";
import PrizeoutButton from "components/bankroll/prizeout-button.tsx";
import { Button } from "react-bootstrap";
import { Bank } from "react-bootstrap-icons";
import { NIL_GROUP_ID } from "utils/constants.ts";
import LoadingOverlay from "components/common/loading-overlay.tsx";
import { useNavigate } from "react-router-dom";
import { getVerificationStatus, getUserState } from "state/slices/user-slice.ts";
import { IDENTITY_VERIFICATION_STATUS } from "interfaces/users-response";
import CommerceButton from "components/commerce/v2/commerce-button.tsx";
import PaymentNote from "components/commerce/v2/payment-note.tsx";
import StyledInteractiveButton from "components/commerce/v2/styled-interactive-button.tsx";

const WithdrawFundsModal = () => {
  const [processing, setProcessing] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | undefined>(undefined);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaynoteFundingSource>();
  const [showAchDetails, setShowAchDetails] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const withdrawModalState = useAppSelector(getWithdrawFundsModalState);
  const realWalletState = useAppSelector(getRealWalletState);
  const verificationStatus = useAppSelector(getVerificationStatus);
  const userState = useAppSelector(getUserState);
  const show = withdrawModalState.show;

  const isBalanceLoading = realWalletState.fetchStatusState === "loading";
  const availableBalance = realWalletState.balance || 0;
  const isIdentityVerified = verificationStatus === IDENTITY_VERIFICATION_STATUS.APPROVED;

  const onClose = useCallback(() => {
    dispatch(hideWithdrawFundsModal());
    dispatch(hideGlobalLoadingOverlay());
    setShowAchDetails(false);
  }, [dispatch]);

  useEffect(() => {
    if (show) {
      setShowAchDetails(withdrawModalState.autoOpenAchSection);
    }
  }, [show, withdrawModalState.autoOpenAchSection]);

  const handleWithdraw = async () => {
    if (!selectedPaymentMethod || !selectedAmount) {
      toast.error('Please select a payment method and amount.');
      return;
    }

    if (selectedAmount < 10) {
      toast.error('Minimum withdrawal amount is $10.');
      return;
    }

    setProcessing(true);
    try {
      await postRequest('/v2/commerce/withdrawal-request', {
        body: {
          amount: selectedAmount,
          paynote_funding_source_id: selectedPaymentMethod.id
        }
      });

      toast.success(`Success! Your withdrawal is in process.`);
      dispatch(fetchRealWallet());
      onClose();
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
            toast.error(errorResponse.friendly_error_message);
            return;
        }
      }
      toast.error("Something went wrong. Please contact support.");
    } finally {
      setProcessing(false);
      dispatch(hideGlobalLoadingOverlay());
    }
  };

  const handlePrizeoutExit = () => {
    dispatch(fetchRealWallet());
    onClose();
  };

  return (
    <SparketModal title="Withdraw Funds" show={show} onClose={onClose}>
      <div style={{ position: "relative" }}>
        <LoadingOverlay 
          show={processing} 
          message="Processing withdrawal..." 
          zIndex={1050}
        />
        
        <GreyModalSection>
          <BalanceDisplay />
        </GreyModalSection>

        {/* Prizeout Section - Prominently displayed at top */}
        <GreyModalSection>
          <div className="d-flex flex-column gap-3">
            <div className="text-center" style={{ color: "white" }}>
              <BlockyBoldText>
                <span style={{ 
                  color: "#ffd700", 
                  fontSize: "1.05em", 
                  fontWeight: "800",
                  textTransform: "uppercase"
                }}>
                  Recommended:
                </span>{" "}
                Cash out with popular gift cards and receive a bonus!
              </BlockyBoldText>
            </div>
            <div className="d-flex justify-content-center">
              <PrizeoutButton
                onExit={handlePrizeoutExit}
                walletId={realWalletState.walletId || ''}
                groupId={NIL_GROUP_ID}
              />
            </div>
          </div>
        </GreyModalSection>

        {/* Divider */}
        <div className="d-flex align-items-center my-3">
          <div className="flex-grow-1" style={{ height: '1px', backgroundColor: "white" }}></div>
          <div className="px-3">
            <BlockyLightText style={{ color: "white" }}>OR</BlockyLightText>
          </div>
          <div className="flex-grow-1" style={{ height: '1px', backgroundColor: "white" }}></div>
        </div>

        {/* ACH Section */}
        {userState.fetchUserState === "succeeded" && (
          <GreyModalSection>
            <div className="mb-3">
              <StyledInteractiveButton
                onClick={() => {
                  if (!isIdentityVerified) {
                    onClose();
                    navigate(`${EDIT_PROFILE_PATH}?returnTo=/profile&openWithdrawModal=true`);
                  } else {
                    setShowAchDetails(!showAchDetails);
                  }
                }}
                variant="expandable"
                expandIcon={<span>{isIdentityVerified ? (showAchDetails ? '−' : '+') : '→'}</span>}
              >
                <Bank size={16} />
                <BlockyBoldText>
                  Bank Transfer {!isIdentityVerified && "(Requires Identity Verification)"}
                </BlockyBoldText>
              </StyledInteractiveButton>
            </div>

            {showAchDetails && (
              <>
                {!isIdentityVerified ? (
                  <div className="d-flex flex-column align-items-center p-3">
                    <BlockyBoldText style={{ color: "white", marginBottom: "1rem", textAlign: "center" }}>
                      Identity verification is required for bank transfers
                    </BlockyBoldText>
                    <LongButton
                      text="Verify Identity To Add New Bank Account"
                      onClick={() => navigate(EDIT_PROFILE_PATH)}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <AmountSelector
                        presetAmounts={[]}
                        selectedAmount={selectedAmount || 0}
                        setSelectedAmount={(amount) => setSelectedAmount(amount)}
                        placeholder="$10 minimum"
                        disabled={isBalanceLoading}
                        maxAmount={availableBalance}
                      />
                      <div className="mt-2 text-center">
                        <BlockyLightText style={{ color: sparketLightGray, fontSize: "0.85em" }}>
                          Minimum withdrawal amount: $10
                        </BlockyLightText>
                      </div>
                    </div>

                    <div className="mb-3">
                      <PaynoteFundingSourceSelector
                        selectedMethod={selectedPaymentMethod}
                        onMethodSelect={setSelectedPaymentMethod}
                        onClose={onClose}
                        returnUrl="/profile?openWithdrawModal=true"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </GreyModalSection>
        )}

        {/* Withdraw Button and Note - Outside of grey section */}
        {showAchDetails && isIdentityVerified && (
          <div className="d-flex flex-column align-items-center">
            <CommerceButton
              onClick={handleWithdraw}
              disabled={processing || selectedAmount === undefined || selectedAmount === 0 || selectedAmount < 10 || !selectedPaymentMethod}
              processing={processing}
              actionType="withdraw"
              amount={selectedAmount}
            />
            <PaymentNote
              processing={processing}
              paymentMethod={selectedPaymentMethod ? {
                type: 'ACH',
                bankName: selectedPaymentMethod.bankName,
                last4: selectedPaymentMethod.last4
              } : undefined}
              actionType="withdraw"
            />
          </div>
        )}
      </div>
    </SparketModal>
  );
};

export default WithdrawFundsModal; 